/**
 * Agent-path formatting for provenance rows and citation tooltips (#20423).
 *
 * `retrieved_by_agent` names the sub-agent (null = the primary assistant), and
 * `primary_retrieval.tool_name` is the internal retrieval discriminator —
 * `tool_name` must NEVER be shown to the user on its own (contract §Searched),
 * only mapped through the provider labels below.
 */

/** Minimal shape formatAgentPath needs — satisfied by ProvenanceSource and by
 * the normalized citation source objects in CitationLink. */
export interface AgentPathSource {
  retrieved_by_agent?: string | null;
  primary_retrieval?: { tool_name?: string | null } | null;
}

/** tool_name → human-facing retrieval-path suffix. Anything unmapped renders
 * no suffix (never the raw tool_name). */
const TOOL_PATH_LABELS: Record<string, string> = {
  adams_content_search: "ADAMS",
  search_doe_technical_reports: "OSTI",
};

/**
 * Human-facing "who retrieved this, via what" string:
 *
 *  - `"AtomAssist (direct)"` when no sub-agent is attributed;
 *  - `"{agent} · ADAMS"` / `"{agent} · OSTI"` when the primary retrieval ran
 *    through a mapped external tool;
 *  - `"{agent}"` otherwise.
 */
export const formatAgentPath = (source: AgentPathSource): string => {
  const agent = source.retrieved_by_agent;
  if (!agent) return "AtomAssist (direct)";
  const toolLabel = TOOL_PATH_LABELS[source.primary_retrieval?.tool_name ?? ""];
  return toolLabel ? `${agent} · ${toolLabel}` : agent;
};
